import React, { useRef, useState, useEffect, useCallback } from 'react';
import { GoogleGenAI, LiveServerMessage, Modality } from '@google/genai';
import { CrackAnalysis, Severity, InspectionMode, TelemetryPoint, Finding, ImageResolutionMode, IMAGE_RESOLUTION_OPTIONS } from '../types';
import { analyzeStaticSurface } from '../services/claudeService';
import { exportFindingsToZip } from '../services/zipService';

interface LiveScannerProps {
  onCancel: () => void;
  onComplete?: (timeline: CrackAnalysis[], startTime: number, lastFrame?: string, trajectory?: TelemetryPoint[]) => void;
  toolSpecs?: string;
  toolFps?: number;
  toolId?: string;
  initialModes?: InspectionMode[];
}

const severityColors: Record<Severity, string> = {
  [Severity.NONE]: '#22c55e',
  [Severity.LOW]: '#0ea5e9',
  [Severity.MEDIUM]: '#eab308',
  [Severity.HIGH]: '#f97316',
  [Severity.CRITICAL]: '#ef4444',
};

const PROTOCOL_OPTIONS = [
  {
    id: InspectionMode.CRACK_ONLY,
    label: 'Crack Detection',
    icon: <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
    desc: 'Cracks & deep scratches.'
  },
  {
    id: InspectionMode.WELD_INTEGRITY,
    label: 'Weld Integrity',
    icon: <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
    desc: 'Junctions & HAZ audit.'
  },
  {
    id: InspectionMode.GENERAL_NDT,
    label: 'Structural Audit',
    icon: <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>,
    desc: 'Cracks, fissures, corrosion & dents.'
  },
  {
    id: InspectionMode.FOULING_INSPECTION,
    label: 'Cleanliness',
    icon: <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.691.31a2 2 0 01-1.782 0l-.691-.31a6 6 0 00-3.86-.517l-2.388.477a2 2 0 00-1.022.547l-.34.34a2 2 0 000 2.829l1.245 1.244a2 2 0 002.829 0l.144-.144a2 2 0 000-2.829l-1.245-1.244" /></svg>,
    desc: 'Residue & Scaling.'
  }
];

const FRAME_RATE = 0.25;
const LIVE_STREAM_RATE = 1;

function decode(base64: string) {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number,
  numChannels: number,
): Promise<AudioBuffer> {
  const dataInt16 = new Int16Array(data.buffer);
  const frameCount = dataInt16.length / numChannels;
  const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);

  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
  }
  return buffer;
}

export const LiveScanner: React.FC<LiveScannerProps> = ({ onCancel, onComplete, toolSpecs, toolId, initialModes = [InspectionMode.GENERAL_NDT] }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);

  const [activeModes, setActiveModes] = useState<InspectionMode[]>(initialModes);
  const activeModesRef = useRef(initialModes);
  const [sensitivity, setSensitivity] = useState(5);
  const sensitivityRef = useRef(5);
  const [resolutionMode, setResolutionMode] = useState<ImageResolutionMode>(ImageResolutionMode.STANDARD);
  const resolutionModeRef = useRef<ImageResolutionMode>(ImageResolutionMode.STANDARD);

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lastResult, setLastResult] = useState<CrackAnalysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessionDetections, setSessionDetections] = useState<CrackAnalysis[]>([]);
  const [sessionStartTime] = useState<number>(Date.now());
  const [lastCapturedFrame, setLastCapturedFrame] = useState<string | undefined>(undefined);

  const [availableDevices, setAvailableDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const streamRef = useRef<MediaStream | null>(null);

  const isiPadUAV = toolId === 'iPad Pro';
  const [liveTrajectory, setLiveTrajectory] = useState<TelemetryPoint[]>([]);

  const [isScanning, setIsScanning] = useState(false);
  const [isExportingZip, setIsExportingZip] = useState(false);

  const audioContextRef = useRef<AudioContext | null>(null);
  const nextStartTimeRef = useRef<number>(0);
  const sessionPromiseRef = useRef<Promise<any> | null>(null);
  const sourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());

  useEffect(() => { activeModesRef.current = activeModes; }, [activeModes]);
  useEffect(() => { sensitivityRef.current = sensitivity; }, [sensitivity]);
  useEffect(() => { resolutionModeRef.current = resolutionMode; }, [resolutionMode]);

  const toggleMode = (mode: InspectionMode) => {
    setActiveModes(prev => {
      if (prev.includes(mode)) {
        if (prev.length === 1) return prev;
        return prev.filter(m => m !== mode);
      }
      return [...prev, mode];
    });
  };

  const initCameraSearch = useCallback(async () => {
    setError(null);
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      const constraints: MediaStreamConstraints = {
        video: isiPadUAV ? { facingMode: { exact: "environment" } } : true
      };
      try {
        const initialStream = await navigator.mediaDevices.getUserMedia(constraints);
        initialStream.getTracks().forEach(track => track.stop());
      } catch (e) {
        console.warn("Could not enforce exact environment mode, falling back to ideal.");
      }
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = allDevices.filter(device => device.kind === 'videoinput');
      setAvailableDevices(videoDevices);
      if (videoDevices.length > 0 && !selectedDeviceId) {
        const backCam = videoDevices.find(d =>
          d.label.toLowerCase().includes('back') ||
          d.label.toLowerCase().includes('rear') ||
          d.label.toLowerCase().includes('environment')
        );
        setSelectedDeviceId(backCam ? backCam.deviceId : videoDevices[0].deviceId);
      } else {
        await navigator.mediaDevices.getUserMedia({ video: true });
      }
    } catch (err: any) {
      setError("Camera permission denied or hardware unavailable.");
    }
  }, [isiPadUAV, selectedDeviceId]);

  const initLiveSession = useCallback(async () => {
    try {
      const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GEMINI_API_KEY });
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
      }
      const ctx = audioContextRef.current;
      const sessionPromise = ai.live.connect({
        model: 'gemini-2.5-flash-native-audio-preview-12-2025',
        callbacks: {
          onopen: () => console.debug('Neural Link established.'),
          onmessage: async (message: LiveServerMessage) => {
            const base64Audio = message.serverContent?.modelTurn?.parts[0]?.inlineData?.data;
            if (base64Audio && ctx) {
              const audioData = decode(base64Audio);
              const audioBuffer = await decodeAudioData(audioData, ctx, 24000, 1);
              nextStartTimeRef.current = Math.max(nextStartTimeRef.current, ctx.currentTime);
              const source = ctx.createBufferSource();
              source.buffer = audioBuffer;
              source.connect(ctx.destination);
              source.start(nextStartTimeRef.current);
              nextStartTimeRef.current += audioBuffer.duration;
              sourcesRef.current.add(source);
              source.onended = () => sourcesRef.current.delete(source);
            }
            if (message.serverContent?.interrupted) {
              sourcesRef.current.forEach(s => s.stop());
              sourcesRef.current.clear();
              nextStartTimeRef.current = 0;
            }
          },
          onerror: (e) => console.error('Neural Link Error:', e),
          onclose: () => console.debug('Neural Link closed.'),
        },
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Kore' } },
          },
          systemInstruction: 'You are an SSTCheck PRO AI Assistant V5.2. Monitor the live video feed. Verbally announce surface defects with technical brevity. Example: "Surface nominal.", "Stress crack detected sector 2."',
        },
      });
      sessionPromiseRef.current = sessionPromise;
    } catch (err) {
      console.warn("Live Audio Link unavailable.", err);
    }
  }, []);

  useEffect(() => {
    initCameraSearch();
    initLiveSession();
    return () => {
      if (sessionPromiseRef.current) {
        sessionPromiseRef.current.then(session => {
          try { session.close(); } catch (e) { }
        });
      }
      if (audioContextRef.current) {
        try { audioContextRef.current.close(); } catch (e) { }
      }
    };
  }, [initCameraSearch, initLiveSession]);

  useEffect(() => {
    let auditInterval: number;
    let liveInterval: number;
    const startCamera = async () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      try {
        const constraints: MediaStreamConstraints = {
          video: {
            deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined,
            facingMode: (isiPadUAV && !selectedDeviceId) ? { exact: 'environment' } : undefined,
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          }
        };
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (e) {
          stream = await navigator.mediaDevices.getUserMedia({ video: true });
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current?.play().catch(e => console.warn("Autoplay blocked:", e));
          };
        }
        auditInterval = window.setInterval(async () => {
          if (!isScanning || isAnalyzing || !videoRef.current || !canvasRef.current) return;
          setIsAnalyzing(true);
          const tempCanvas = document.createElement('canvas');
          const video = videoRef.current;

          // Respect selected resolution mode for output frames
          const targetRes = IMAGE_RESOLUTION_OPTIONS.find(o => o.mode === resolutionModeRef.current) || IMAGE_RESOLUTION_OPTIONS[0];
          const targetWidth = Math.min(video.videoWidth, targetRes.px);
          const ratio = targetWidth / video.videoWidth;
          const targetHeight = Math.round(video.videoHeight * ratio);

          tempCanvas.width = targetWidth;
          tempCanvas.height = targetHeight;
          const ctx = tempCanvas.getContext('2d', { alpha: false });
          if (ctx) {
            ctx.drawImage(video, 0, 0, tempCanvas.width, tempCanvas.height);
            const frameBase64 = tempCanvas.toDataURL('image/jpeg', 0.85);
            setLastCapturedFrame(frameBase64);
            try {
              const currentModes = activeModesRef.current;
              const currentSensitivity = sensitivityRef.current;
              const result = await analyzeStaticSurface(frameBase64, currentModes, toolSpecs, currentSensitivity, 5000, 'gemini-2.5-pro', '', undefined, resolutionModeRef.current);
              const enriched: CrackAnalysis = {
                ...result,
                timestamp: (Date.now() - sessionStartTime) / 1000,
                frameImage: frameBase64,
                videoName: 'Live Audit Snapshot',
                mode: result.mode || currentModes[0],
                diagnosticNote: result.diagnosticNote || "High-precision live frame baseline."
              };
              setLastResult(enriched);
              if (result.detected) {
                setSessionDetections(prev => [enriched, ...prev]);
              }
            } catch (err) { console.error(err); }
            finally { setIsAnalyzing(false); }
          } else {
            setIsAnalyzing(false);
          }
        }, 1000 / FRAME_RATE);
        liveInterval = window.setInterval(() => {
          if (!sessionPromiseRef.current || !videoRef.current || !canvasRef.current) return;
          const tempCanvas = document.createElement('canvas');
          const video = videoRef.current;
          tempCanvas.width = 640;
          tempCanvas.height = Math.round(640 / (video.videoWidth / video.videoHeight));
          const ctx = tempCanvas.getContext('2d', { alpha: false });
          if (ctx) {
            ctx.drawImage(video, 0, 0, tempCanvas.width, tempCanvas.height);
            const data = tempCanvas.toDataURL('image/jpeg', 0.5).split(',')[1];
            sessionPromiseRef.current.then((session) => {
              session.sendRealtimeInput({ media: { data, mimeType: 'image/jpeg' } });
            });
          }
        }, 1000 / LIVE_STREAM_RATE);
      } catch (err) { setError("Camera permission denied or hardware unavailable."); }
    };
    if (selectedDeviceId || availableDevices.length === 0) {
      startCamera();
    }
    return () => {
      clearInterval(auditInterval);
      clearInterval(liveInterval);
      if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
    };
  }, [selectedDeviceId, toolSpecs, sessionStartTime, isScanning, isiPadUAV]);

  useEffect(() => {
    const render = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas) {
        if (video.paused && video.srcObject) {
          video.play().catch(() => { });
        }
        if (!video.paused && !video.ended) {
          const ctx = canvas.getContext('2d');
          if (ctx) {
            if (canvas.width !== video.videoWidth) {
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
            }
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            if (isScanning) {
              ctx.fillStyle = 'red';
              ctx.beginPath();
              ctx.arc(30, 30, 8, 0, Math.PI * 2);
              ctx.fill();
              const scanY = (Date.now() % 2000) / 2000 * canvas.height;
              ctx.strokeStyle = 'rgba(34, 197, 94, 0.6)';
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.moveTo(0, scanY);
              ctx.lineTo(canvas.width, scanY);
              ctx.stroke();
            }
            if (lastResult && lastResult.detected && lastResult.findings) {
              const age = Date.now() - (sessionStartTime + (lastResult.timestamp || 0) * 1000);
              if (age < 2000) {
                lastResult.findings.forEach(f => {
                  if (f.box) {
                    const [ymin, xmin, ymax, xmax] = f.box;
                    const color = severityColors[f.severity] || '#f97316';
                    const x = (xmin / 1000) * canvas.width;
                    const y = (ymin / 1000) * canvas.height;
                    const w = ((xmax - xmin) / 1000) * canvas.width;
                    const h = ((ymax - ymin) / 1000) * canvas.height;
                    ctx.strokeStyle = color;
                    ctx.lineWidth = 4;
                    ctx.strokeRect(x, y, w, h);
                    ctx.fillStyle = color;
                    ctx.font = 'bold 20px monospace';
                    ctx.fillText(f.type.toUpperCase(), x, y - 10);
                  }
                });
              }
            }
          }
        }
      }
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isScanning, lastResult, sessionStartTime]);

  const toggleScan = () => {
    if (isScanning) {
      setIsScanning(false);
    } else {
      setSessionDetections([]);
      setIsScanning(true);
    }
  };

  const handleZipDownload = async () => {
    if (sessionDetections.length === 0) return;
    setIsExportingZip(true);
    try {
      const allFindings: Finding[] = sessionDetections.flatMap(analysis =>
        (analysis.findings || []).map(f => ({ ...f, frameImage: analysis.frameImage }))
      );
      await exportFindingsToZip(allFindings, `Live_Session_Evidence`);
    } catch (e) { console.error(e); alert("Failed to generate ZIP archive."); } finally { setIsExportingZip(false); }
  };

  const handleContinue = () => {
    if (onComplete) {
      onComplete(sessionDetections, sessionStartTime, lastCapturedFrame, liveTrajectory);
    }
  };


  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-slate-300 p-12 text-center animate-fadeIn">
        <h2 className="text-2xl font-black text-slate-900 uppercase tracking-widest mb-4">HARDWARE FAULT DETECTED</h2>
        <p className="text-slate-800 text-sm mb-12 italic font-medium">"{error}"</p>
        <div className="flex gap-4">
          <button onClick={() => initCameraSearch()} className="px-8 py-5 bg-[#0000a9] text-white font-black uppercase text-xs tracking-[0.2em] transition-all shadow-2xl active:scale-95">
            RETRY CONNECTION
          </button>
          <button onClick={onCancel} className="px-8 py-5 bg-slate-300 hover:bg-slate-400 text-slate-700 hover:text-[#0000a9] font-black uppercase text-xs tracking-[0.2em] transition-all shadow-lg active:scale-95 border border-slate-400">
            RETURN TO SAFETY
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-300">
      <div className="p-4 border-b border-slate-500 flex justify-between items-center bg-slate-400 shadow-sm">
        <div className="flex flex-col text-left">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-none ${isScanning ? 'bg-red-500 animate-pulse' : 'bg-[#0000a9]/40'}`}></div>
            <h3 className="font-black text-slate-900 uppercase tracking-[0.2em] text-[10px] italic">Mission Control | Live Scanner V5.2</h3>
          </div>
        </div>
        <button onClick={onCancel} className="text-slate-700 hover:text-[#0000a9] transition uppercase text-[10px] font-black tracking-widest bg-slate-300 px-6 py-2 border border-slate-400 shadow-sm">Abort mission</button>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col p-4 bg-slate-300 relative">
        <div className="flex w-full h-full gap-4">
          <div className="flex-1 relative flex items-center justify-center overflow-hidden bg-black/5 border border-slate-500 shadow-lg group">
            <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover opacity-0 pointer-events-none" muted playsInline autoPlay />
            <canvas ref={canvasRef} className="w-full h-full object-contain" />
            <div className="absolute top-6 left-6 flex flex-col gap-2">
              <div className="bg-slate-300/90 backdrop-blur-md px-3 py-1.5 rounded-none text-[9px] font-black text-slate-800 uppercase tracking-widest border border-slate-500 flex items-center gap-2 w-fit shadow-sm">
                <div className={`w-2 h-2 rounded-none ${isScanning ? 'bg-red-600 animate-pulse' : 'bg-slate-400'}`}></div>
                {isScanning ? 'LIVE FEED ACTIVE' : 'STANDBY'}
              </div>
              {isAnalyzing && (
                <div className="bg-slate-300/90 backdrop-blur-md px-3 py-1.5 rounded-none text-[9px] font-black text-[#0000a9] uppercase tracking-widest border border-[#0000a9]/30 w-fit shadow-sm">
                  NEURAL LINK: PROCESSING
                </div>
              )}
            </div>
          </div>
          <div className="flex-1 relative bg-slate-400 border border-slate-500 overflow-hidden shadow-lg flex flex-col p-6">
            <div className="flex justify-between items-center mb-4 border-b border-slate-500 pb-2">
              <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-[0.2em] italic">Live Anomaly Feed</h4>
              <span className="text-[9px] bg-slate-300 text-slate-700 px-2 py-1 border border-slate-400 font-bold uppercase">{sessionDetections.length} Events</span>
            </div>

            {sessionDetections.length > 0 ? (
              <>
                <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    {sessionDetections.map((det, i) => (
                      <div key={i} className="aspect-video relative rounded-none overflow-hidden border border-slate-500 group bg-slate-300">
                        <img src={det.frameImage} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" alt="Anomaly" />
                        <div className={`absolute top-1 left-1 bg-slate-300/90 backdrop-blur px-1.5 py-0.5 rounded-none text-[7px] font-black text-slate-800 uppercase border border-slate-500 ${det.severity === Severity.CRITICAL ? 'text-red-600' : ''}`}>
                          {det.findings?.[0]?.type || 'Anomaly'}
                        </div>
                        <div className={`absolute bottom-0 inset-x-0 h-1 ${det.severity === Severity.CRITICAL ? 'bg-red-500' : det.severity === Severity.HIGH ? 'bg-orange-500' : 'bg-yellow-500'}`}></div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-4 pt-2 border-t border-slate-500 grid grid-cols-2 gap-3">
                  <button
                    onClick={handleZipDownload}
                    disabled={isExportingZip}
                    className="w-full py-3 bg-[#0000a9]/10 text-[#0000a9] border border-[#0000a9]/20 rounded-none font-black uppercase text-[9px] tracking-widest transition shadow-sm hover:bg-[#0000a9]/20 flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                    {isExportingZip ? 'Compressing...' : 'Download ZIP'}
                  </button>
                  <button
                    onClick={handleContinue}
                    className="w-full py-3 bg-[#0000a9] text-white rounded-none font-black uppercase text-[9px] tracking-widest transition shadow-md hover:bg-[#0000c0] flex items-center justify-center gap-2"
                  >
                    Continue
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                  </button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center opacity-30">
                <div className="w-16 h-16 border-2 border-dashed border-slate-600 rounded-2xl flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                </div>
                <span className="text-slate-800 text-[10px] uppercase font-black tracking-widest italic">Awaiting Detection Data</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-8 bg-slate-400 border-t border-slate-500 space-y-10 overflow-y-auto max-h-[50vh] custom-scrollbar text-left shadow-2xl z-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="space-y-4">
            <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-[0.4em] border-b border-slate-500 pb-2 italic">Switch Mission Protocol</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
              {PROTOCOL_OPTIONS.map(protocol => {
                const isActive = activeModes.includes(protocol.id);
                return (
                  <button
                    key={protocol.id}
                    onClick={() => !isScanning && toggleMode(protocol.id)}
                    disabled={isScanning}
                    className={`relative group aspect-video p-6 border text-left transition-all overflow-hidden flex flex-col justify-between ${isActive ? 'bg-[#0000a9] border-[#0000a9] shadow-[0_5px_15px_rgba(0,0,169,0.2)]' : 'bg-slate-300 border-slate-400 hover:border-[#0000a9]/30 hover:bg-slate-400'} ${isScanning ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className={`transition-colors duration-300 ${isActive ? 'text-white' : 'text-slate-800 group-hover:text-[#0000a9]'}`}>
                      {protocol.icon}
                    </div>
                    <div>
                      <h3 className={`font-black text-[10px] uppercase tracking-wide leading-none ${isActive ? 'text-white' : 'text-slate-900'}`}>
                        {protocol.label}
                      </h3>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-[0.4em] border-b border-slate-500 pb-3 italic">Audit Parameters</h4>

            <div className="space-y-6 pt-2">
              <div className="flex justify-between text-[11px] font-mono font-black"><span className="text-slate-700 uppercase">Audit Sensitivity</span><span className="text-[#0000a9]">LVL {sensitivity}</span></div>
              <input type="range" min="1" max="10" step="1" value={sensitivity} onChange={(e) => setSensitivity(parseInt(e.target.value))} className="w-full h-1.5 bg-white rounded-none appearance-none cursor-pointer accent-[#0000a9]" />

              <div className="space-y-3 pt-2">
                <div className="text-[11px] font-mono font-black text-slate-700 uppercase">Neural Resolution</div>
                <div className="flex flex-col gap-2">
                  {IMAGE_RESOLUTION_OPTIONS.map(opt => (
                    <button
                      key={opt.mode}
                      onClick={() => !isScanning && setResolutionMode(opt.mode)}
                      disabled={isScanning}
                      className={`w-full text-left px-4 py-2.5 border transition-all flex justify-between items-center rounded-none shadow-sm ${resolutionMode === opt.mode
                        ? 'bg-[#0000a9] border-[#0000a9] text-white'
                        : 'bg-slate-300 border-slate-400 text-slate-800 hover:border-[#0000a9]/60'
                        } ${isScanning ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className="text-[10px] font-black uppercase tracking-widest">{opt.label}</span>
                      <span className={`text-[9px] font-bold uppercase tracking-tight ${resolutionMode === opt.mode ? 'text-white/70' : 'text-slate-600'}`}>{opt.desc.split('·').pop()?.trim()}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <div className="flex justify-between text-[11px] font-mono font-black"><span className="text-slate-700 uppercase">Input Source</span></div>
              <select
                value={selectedDeviceId}
                onChange={(e) => !isScanning && setSelectedDeviceId(e.target.value)}
                disabled={isScanning}
                className="w-full bg-slate-300 border border-slate-400 text-slate-800 text-xs rounded-none p-4 outline-none focus:border-[#0000a9] disabled:opacity-50 font-black uppercase tracking-widest"
              >
                {availableDevices.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${d.deviceId.slice(0, 5)}...`}</option>)}
              </select>
            </div>
          </div>
        </div>

        <button
          onClick={toggleScan}
          className={`w-full py-8 font-black uppercase text-[14px] tracking-[0.5em] shadow-2xl transition-all active:scale-[0.99] ${isScanning ? 'bg-red-700 hover:bg-red-600 text-white' : 'bg-[#0000a9] text-white hover:bg-[#0000c0]'}`}
        >
          {isScanning ? 'TERMINATE TECHNICAL SCAN' : 'INITIATE GEA INSPECTION'}
        </button>
      </div>
    </div>
  );
};